import { useState, useEffect } from 'react';
import { useNavigation } from '@refinedev/core';
import { List } from '@refinedev/antd';
import { Table, Button, Tag, Space, Avatar, Input, message, Popconfirm, Modal } from 'antd';
import {
  PlusOutlined,
  BankOutlined,
  EyeOutlined,
  SearchOutlined,
  StopOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import { apiClient } from '../../lib/api-client';

interface BusCompanyStaff {
  id: number;
  name: string;
  legalName: string;
  username: string;
  contactPhone: string;
  contactEmail: string | null;
  status: 'ONLINE' | 'OFFLINE' | 'BLOCKED';
  commissionPercent: number;
  balance: number;
  createdAt: string;
  _count?: {
    busTrips: number;
  };
}

const statusColors: Record<string, string> = {
  ONLINE: 'green',
  OFFLINE: 'orange',
  BLOCKED: 'red',
};

const statusLabels: Record<string, string> = {
  ONLINE: 'Faol',
  OFFLINE: 'Oflayn',
  BLOCKED: 'Bloklangan',
};

export const BusStaffList = () => {
  const { create, show } = useNavigation();
  const [data, setData] = useState<BusCompanyStaff[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [blockModalOpen, setBlockModalOpen] = useState(false);
  const [blockReason, setBlockReason] = useState('');
  const [blockingId, setBlockingId] = useState<number | null>(null);
  const [blocking, setBlocking] = useState(false);

  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/admin/bus/companies', {
        params: { search: search || undefined },
      });
      setData(response.data.data || []);
    } catch {
      message.error("Ma'lumotlarni yuklashda xatolik");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  const handleSearch = () => {
    fetchCompanies();
  };

  const openBlockModal = (id: number) => {
    setBlockingId(id);
    setBlockReason('');
    setBlockModalOpen(true);
  };

  const handleBlock = async () => {
    if (!blockReason.trim()) {
      message.error('Sabab kiritilishi shart');
      return;
    }
    if (!blockingId) return;

    setBlocking(true);
    try {
      await apiClient.post(`/admin/bus/companies/${blockingId}/block`, { reason: blockReason });
      message.success('Kompaniya bloklandi');
      setBlockModalOpen(false);
      setBlockReason('');
      setBlockingId(null);
      fetchCompanies();
    } catch {
      message.error('Xatolik yuz berdi');
    } finally {
      setBlocking(false);
    }
  };

  const handleUnblock = async (id: number) => {
    try {
      await apiClient.post(`/admin/bus/companies/${id}/unblock`);
      message.success('Kompaniya faollashtirildi');
      fetchCompanies();
    } catch {
      message.error('Xatolik yuz berdi');
    }
  };

  const columns = [
    {
      title: 'Kompaniya',
      key: 'company',
      render: (_: unknown, record: BusCompanyStaff) => (
        <Space>
          <Avatar
            icon={<BankOutlined />}
            style={{
              backgroundColor:
                record.status === 'BLOCKED' ? '#E74C3C' :
                record.status === 'ONLINE' ? '#27AE60' : '#95A5A6',
            }}
          />
          <div>
            <div style={{ fontWeight: 500 }}>{record.name}</div>
            <div style={{ color: '#7F8C8D', fontSize: 12 }}>{record.legalName}</div>
          </div>
        </Space>
      ),
    },
    {
      title: 'Login',
      dataIndex: 'username',
      key: 'username',
      render: (username: string) => (
        <code style={{ background: '#f5f5f5', padding: '2px 8px', borderRadius: 4 }}>
          {username}
        </code>
      ),
    },
    {
      title: 'Telefon',
      dataIndex: 'contactPhone',
      key: 'contactPhone',
    },
    {
      title: 'Komissiya',
      dataIndex: 'commissionPercent',
      key: 'commissionPercent',
      render: (val: number) => `${val}%`,
      width: 100,
    },
    {
      title: 'Reyslar',
      key: 'trips',
      render: (_: unknown, record: BusCompanyStaff) => record._count?.busTrips ?? 0,
      width: 80,
    },
    {
      title: 'Holat',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={statusColors[status]}>{statusLabels[status]}</Tag>
      ),
      width: 110,
    },
    {
      title: 'Yaratilgan',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => new Date(date).toLocaleDateString('uz-UZ'),
      width: 110,
    },
    {
      title: 'Amallar',
      key: 'actions',
      width: 140,
      render: (_: unknown, record: BusCompanyStaff) => (
        <Space>
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => show('admin/bus/companies', record.id)}
          />
          {record.status === 'BLOCKED' ? (
            <Popconfirm
              title="Kompaniyani faollashtirasizmi?"
              onConfirm={() => handleUnblock(record.id)}
            >
              <Button type="text" icon={<CheckOutlined />} style={{ color: '#27AE60' }} />
            </Popconfirm>
          ) : (
            <Button type="text" icon={<StopOutlined />} danger onClick={() => openBlockModal(record.id)} />
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
    <List
      title="Kompaniyalar boshqaruvi"
      headerButtons={
        <Space>
          <Input
            placeholder="Qidirish..."
            prefix={<SearchOutlined />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onPressEnter={handleSearch}
            style={{ width: 220 }}
            allowClear
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => create('bus/staff')}
          >
            Yangi kompaniya
          </Button>
        </Space>
      }
    >
      <Table
        dataSource={data}
        columns={columns}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 20, showTotal: (total) => `Jami: ${total}` }}
      />
    </List>

    {/* Block Reason Modal */}
    <Modal
      title="Kompaniyani bloklash"
      open={blockModalOpen}
      onCancel={() => {
        setBlockModalOpen(false);
        setBlockReason('');
        setBlockingId(null);
      }}
      onOk={handleBlock}
      okText="Bloklash"
      cancelText="Bekor qilish"
      okButtonProps={{ danger: true, loading: blocking }}
    >
      <p style={{ marginBottom: 16 }}>Bloklash sababini kiriting:</p>
      <Input.TextArea
        value={blockReason}
        onChange={(e) => setBlockReason(e.target.value)}
        placeholder="Bloklash sababi..."
        rows={4}
      />
    </Modal>
    </>
  );
};
